import React, { memo, useCallback, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Container,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ThemeProvider,
  Typography,
  createTheme,
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import dayjs from 'dayjs';
import { pageColors } from './HomePage';
import apiFetch from '../api/apiFetch';

const ORDERS_THEME = createTheme({
  palette: {
    primary: {
      main: pageColors.orders,
    },
  },
});

const PAGE_BACKGROUND =
  'radial-gradient(circle at top left, rgba(56,142,60,0.16), transparent 55%), #f5f7fb';

const SURFACE_SX = {
  borderRadius: 4,
  backgroundColor: '#fff',
  boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
  border: '1px solid rgba(0,0,0,0.05)',
};

const DATE_FILTER_FIELD_SX = {
  width: { xs: '100%', sm: 190 },
  flex: { sm: '0 0 auto' },
};

const DATE_FILTER_INPUT_SX = {
  ...DATE_FILTER_FIELD_SX,
  '& .MuiInputBase-root': {
    height: 40,
  },
};

const FILTER_SUMMARY_ITEM_SX = {
  borderRadius: 3,
  border: '1px solid rgba(0,0,0,0.06)',
  backgroundColor: 'rgba(56,142,60,0.04)',
  px: 1.25,
  py: 1,
};

const TABLE_HEADER_CELL_SX = {
  fontWeight: 700,
  backgroundColor: '#fafafa',
};

const formatRangeDate = (value) => {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD MMM YYYY') : String(value ?? '-');
};

const formatLoadedRange = (range) => {
  if (!range?.start || !range?.end) {
    return 'No range loaded yet';
  }

  return `${formatRangeDate(range.start)} to ${formatRangeDate(range.end)}`;
};

const formatSummaryTotal = (value) =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const compareNullableNumbers = (left, right, order) => {
  if (left === null && right === null) {
    return 0;
  }
  if (left === null) {
    return 1;
  }
  if (right === null) {
    return -1;
  }
  return order === 'asc' ? left - right : right - left;
};

const OrdersTable = memo(function OrdersTable({
  rows,
  order,
  orderBy,
  onRequestSort,
  loading,
  emptyMessage,
}) {
  return (
    <Paper elevation={0} sx={{ ...SURFACE_SX, p: 0, overflow: 'hidden' }}>
      {loading && <LinearProgress />}
      <TableContainer sx={{ maxHeight: '70vh' }}>
        <Table stickyHeader aria-label="orders table" sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...TABLE_HEADER_CELL_SX, width: '18%' }}>
                <TableSortLabel
                  active={orderBy === 'orderNumber'}
                  direction={orderBy === 'orderNumber' ? order : 'asc'}
                  onClick={() => onRequestSort('orderNumber')}
                >
                  Order #
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_CELL_SX, width: '40%' }}>
                <TableSortLabel
                  active={orderBy === 'customerName'}
                  direction={orderBy === 'customerName' ? order : 'asc'}
                  onClick={() => onRequestSort('customerName')}
                >
                  Customer
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ ...TABLE_HEADER_CELL_SX, width: '20%' }}>
                <TableSortLabel
                  active={orderBy === 'orderDate'}
                  direction={orderBy === 'orderDate' ? order : 'asc'}
                  onClick={() => onRequestSort('orderDate')}
                >
                  Order Date
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sx={{ ...TABLE_HEADER_CELL_SX, width: '22%' }}>
                <TableSortLabel
                  active={orderBy === 'total'}
                  direction={orderBy === 'total' ? order : 'asc'}
                  onClick={() => onRequestSort('total')}
                >
                  Total
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length > 0 ? (
              rows.map((row) => (
                <TableRow
                  key={row.key}
                  hover
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(56,142,60,0.04)',
                    },
                  }}
                >
                  <TableCell>{row.orderNumberDisplay}</TableCell>
                  <TableCell>{row.customerNameDisplay}</TableCell>
                  <TableCell>{row.orderDateDisplay}</TableCell>
                  <TableCell align="right">{row.totalDisplay}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
});

export default function OrdersPage({ connected, setConnected, setError }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [orders, setOrders] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('orderNumber');
  const [loading, setLoading] = useState(false);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const [appliedRange, setAppliedRange] = useState(null);

  const orderRows = useMemo(
    () =>
      Array.isArray(orders)
        ? orders.filter((item) => item !== null && item !== undefined).map((item, index) => {
            const parsedDate = dayjs(item.orderDate);
            const numericTotal = Number(item.total);

            return {
              key: item.id ?? item.orderNumber ?? `order-${index}`,
              orderNumberDisplay: item.orderNumber ?? '-',
              customerNameDisplay: item.customerName ?? '-',
              orderDateDisplay: item.orderDate ?? '-',
              totalDisplay: item.total ?? '-',
              sortOrderNumber: String(item.orderNumber ?? '').toLowerCase(),
              sortCustomerName: String(item.customerName ?? '').toLowerCase(),
              sortOrderDate: parsedDate.isValid() ? parsedDate.valueOf() : null,
              sortTotal: Number.isFinite(numericTotal) ? numericTotal : null,
            };
          })
        : [],
    [orders]
  );

  const sortedRows = useMemo(() => {
    const comparator = (left, right) => {
      switch (orderBy) {
        case 'orderNumber':
          return order === 'asc'
            ? left.sortOrderNumber.localeCompare(right.sortOrderNumber, undefined, {
                numeric: true,
              })
            : right.sortOrderNumber.localeCompare(left.sortOrderNumber, undefined, {
                numeric: true,
              });
        case 'customerName':
          return order === 'asc'
            ? left.sortCustomerName.localeCompare(right.sortCustomerName)
            : right.sortCustomerName.localeCompare(left.sortCustomerName);
        case 'orderDate':
          return compareNullableNumbers(left.sortOrderDate, right.sortOrderDate, order);
        case 'total':
          return compareNullableNumbers(left.sortTotal, right.sortTotal, order);
        default:
          return 0;
      }
    };

    return [...orderRows].sort(comparator);
  }, [orderRows, order, orderBy]);

  const totalOrderValue = useMemo(
    () => orderRows.reduce((sum, row) => sum + (row.sortTotal ?? 0), 0),
    [orderRows]
  );

  const loadedRangeLabel = useMemo(() => formatLoadedRange(appliedRange), [appliedRange]);

  const summaryItems = useMemo(
    () => [
      {
        label: 'Loaded Orders',
        value: hasFetchedData ? orderRows.length.toLocaleString() : '-',
      },
      {
        label: 'Total Order Value',
        value: hasFetchedData ? formatSummaryTotal(totalOrderValue) : '-',
      },
      {
        label: 'Loaded Range',
        value: loadedRangeLabel,
      },
    ],
    [hasFetchedData, orderRows.length, totalOrderValue, loadedRangeLabel]
  );

  const emptyMessage = useMemo(() => {
    if (loading && !hasFetchedData) {
      return 'Loading orders...';
    }

    if (!hasFetchedData) {
      return 'Pick a date range and click Search to load orders.';
    }

    return `No orders found for ${loadedRangeLabel}.`;
  }, [hasFetchedData, loadedRangeLabel, loading]);

  const handleRequestSort = useCallback(
    (property) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [order, orderBy]
  );

  const handleSearch = async () => {
    if (!connected) {
      setError('Not connected to Fortnox. Please connect first.');
      setConnected(false);
      return;
    }

    if (!startDate) {
      setError('Please pick a start date.');
      return;
    }

    if (!endDate) {
      setError('Please pick an end date.');
      return;
    }

    const normalizedStartDate = dayjs(startDate);
    const normalizedEndDate = dayjs(endDate);

    if (!normalizedStartDate.isValid()) {
      setError('Please pick a start date.');
      return;
    }

    if (!normalizedEndDate.isValid()) {
      setError('Please pick an end date.');
      return;
    }

    if (normalizedStartDate.isAfter(normalizedEndDate, 'day')) {
      setError('Start date must be on or before end date.');
      return;
    }

    if (normalizedEndDate.diff(normalizedStartDate, 'day') > 31) {
      setError('Search period cannot be longer than 31 days.');
      return;
    }

    const startDateParam = normalizedStartDate.format('YYYY-MM-DD');
    const endDateParam = normalizedEndDate.format('YYYY-MM-DD');
    const queryParams = new URLSearchParams({
      startDate: startDateParam,
      endDate: endDateParam,
    });

    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch(`/api/orders?${queryParams.toString()}`);

      if (!response.ok) {
        let errorMessage;

        if (response.status === 401) {
          errorMessage =
            'Failed to load orders: Unauthorized. Please reconnect to Fortnox and try again.';
          setConnected(false);
        } else if (response.status === 429) {
          errorMessage = 'Fortnox cannot handle requests at the moment. Please wait.';
          setConnected(true);
        } else {
          errorMessage = 'Failed to fetch orders. Please try again.';
          setConnected(false);
        }

        setError(errorMessage);
        return;
      }

      const data = await response.json();
      const nextOrders = Array.isArray(data) ? data : [];

      setOrders(nextOrders);
      setAppliedRange({
        start: startDateParam,
        end: endDateParam,
      });
      setHasFetchedData(true);
      setConnected(true);
    } catch (error) {
      console.error('Failed to load orders:', error);
      setError('Unable to load orders right now. Please try again.');
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={ORDERS_THEME}>
      <Box
        sx={{
          minHeight: '100vh',
          background: PAGE_BACKGROUND,
          py: { xs: 3, md: 4 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={2.25}>
            <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 1.5, md: 2 } }}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: 'rgba(56,142,60,0.14)',
                    color: pageColors.orders,
                  }}
                >
                  <AssignmentTurnedInIcon fontSize="large" />
                </Avatar>

                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="overline" sx={{ color: pageColors.orders, letterSpacing: 3 }}>
                    ORDERS
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
                    Orders
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 0.25 }}>
                    Review order activity for a selected Fortnox date range.
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 1.5, md: 1.75 } }}>
              <Stack spacing={1.25}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Filters
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose a date range, load the matching orders, and review the latest result.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      lg: 'minmax(0, 1fr) auto',
                    },
                    gap: 1.25,
                    alignItems: 'start',
                  }}
                >
                  <Stack spacing={1.25} sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.25,
                        alignItems: 'start',
                      }}
                    >
                      <TextField
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={(event) => setStartDate(event.target.value)}
                        size="small"
                        fullWidth
                        sx={DATE_FILTER_INPUT_SX}
                        InputLabelProps={{ shrink: true }}
                      />

                      <TextField
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={(event) => setEndDate(event.target.value)}
                        size="small"
                        fullWidth
                        sx={DATE_FILTER_INPUT_SX}
                        InputLabelProps={{ shrink: true }}
                      />

                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        sx={{
                          width: { xs: '100%', sm: 'auto' },
                          flex: { sm: '0 0 auto' },
                        }}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleSearch}
                          disabled={loading}
                          sx={{ minHeight: 38, minWidth: { sm: 136 } }}
                        >
                          {loading ? 'Loading...' : 'Search'}
                        </Button>
                      </Stack>
                    </Box>
                  </Stack>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{
                      width: { xs: '100%', lg: 'auto' },
                      justifySelf: { lg: 'end' },
                    }}
                  >
                    {summaryItems.map((item) => (
                      <Box
                        key={item.label}
                        sx={{
                          ...FILTER_SUMMARY_ITEM_SX,
                          width: { xs: '100%', sm: 'auto' },
                          minWidth: { sm: item.label === 'Loaded Range' ? 220 : 160 },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            fontWeight: 700,
                            lineHeight: 1.2,
                            color: pageColors.orders,
                          }}
                        >
                          {item.label}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="text.primary">
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Stack>
            </Paper>

            <OrdersTable
              rows={sortedRows}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              loading={loading}
              emptyMessage={emptyMessage}
            />
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
